import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import {
  selectCreateNewProject,
  selectSelectedGroup,
  selectSelectedProject,
  selectProjectsInGroup,
  selectNewProjectName,
  setCreateNewProject,
  setSelectedGroup,
  setSelectedProject,
  setProjectsInGroup,
  setNewProjectName,
} from 'app/sharedSlice';
// import { Switch } from '@headlessui/react';
import ComboboxAutocomplete from './ComboboxAutocomplete';
import InformationTooltip from 'components/InformationTooltip';
import type { IGroup, IProject } from 'utils/types';
import { getConfig } from 'config/config-helper';
import { classNames } from 'utils/styleUtils';
import { isProjectNameValid } from 'utils/formatUtils';

const { dataopsliveBaseUrl } = getConfig();

export const getProjectNameError = (projectName: string): string => {
  if (projectName.length === 0) {
    return '';
  }

  if (projectName.length === 1 && !/^[a-zA-Z0-9]$/.test(projectName)) {
    return 'Single character project names must be alphanumeric';
  }

  if (!/^[a-zA-Z0-9]/.test(projectName)) {
    return 'Project name must start with an alphanumeric character';
  }

  if (!/[a-zA-Z0-9]$/.test(projectName)) {
    return 'Project name must end with an alphanumeric character';
  }

  if (/[_.-]{2}/.test(projectName)) {
    return 'Project name cannot contain consecutive special characters (_, ., -)';
  }

  if (/\s{2}/.test(projectName)) {
    return 'Project name cannot contain consecutive spaces';
  }

  if (/\.(git|atom)$/.test(projectName)) {
    return 'Project name cannot end with .git or .atom';
  }

  if (!/^[a-zA-Z0-9._\s-]*$/.test(projectName)) {
    return 'Project name can only contain alphanumeric characters, dots, underscores, spaces, and hyphens';
  }

  if (projectName.length > 255) {
    return 'Project name must be less than 255 characters';
  }

  if (!isProjectNameValid(projectName)) {
    return 'Invalid project name format';
  }

  return '';
};

interface GroupAndProjectSelectorProps {
  showCreateNewProject?: boolean;
  showProjectSelector?: boolean;
  projectSelectorDisabled?: boolean;
  onProjectSelectionChanged?: () => void;
  onProjectSearchChanged?: () => void;
}

const GroupAndProjectSelector = (props: GroupAndProjectSelectorProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const createNewProject = useAppSelector(selectCreateNewProject);
  const selectedGroup = useAppSelector(selectSelectedGroup);
  const selectedProject = useAppSelector(selectSelectedProject);
  const projectsInGroup = useAppSelector(selectProjectsInGroup);
  const newProjectName = useAppSelector(selectNewProjectName);

  const [groups, setGroups] = useState<IGroup[]>([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectNameError, setProjectNameError] = useState<string>('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /* eslint-disable @typescript-eslint/strict-boolean-expressions */
  useEffect(() => {
    if (selectedGroup === null || selectedGroup.name === '') {
      return;
    }

    axios
      .get(
        `${dataopsliveBaseUrl}/api/v4/groups/${selectedGroup.id}/projects?min_access_level=30&search=${projectSearchTerm}&order_by=similarity`,
        {
          withCredentials: false,
        },
      )
      .then((response) => {
        console.log(response.data);
        dispatch(
          setProjectsInGroup(
            response.data.map((project: any) => ({
              name: project.name,
              id: project.id,
              path: project.path_with_namespace,
            })),
          ),
        );
      })
      .catch((error) => {
        console.log(error);
      });
  }, [selectedGroup, projectSearchTerm]);

  useEffect(() => {
    axios
      .get(`${dataopsliveBaseUrl}/api/v4/groups?min_access_level=30&search=${groupSearchTerm}&order_by=similarity`, {
        withCredentials: false,
      })
      .then((response) => {
        console.log(response.data);
        setGroups(
          response.data.map((group: any) => ({
            name: group.name,
            id: group.id,
            path: group.full_path,
          })),
        );
      })
      .catch((error) => {
        console.log(error);
      });
  }, [groupSearchTerm]);

  useEffect(() => {
    if (groups.length === 1) {
      dispatch(setSelectedGroup(groups[0]));
    }
  }, [groups]);

  useEffect(() => {
    if (projectsInGroup.length === 1) {
      dispatch(setSelectedProject(projectsInGroup[0]));
    }
  }, [projectsInGroup]);

  return (
    <>
      {props.showCreateNewProject !== false && (
        //   <Field as="div" className="col-span-2 flex items-center justify-center">
        //     <Switch
        //       checked={createNewProject}
        //       onChange={() => {
        //         dispatch(setCreateNewProject(!createNewProject));
        //         if (props.onProjectSelectionChanged) {
        //           props.onProjectSelectionChanged();
        //         }
        //       }}
        //       className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
        //     >
        //       <span className="sr-only">Use setting</span>
        //       <span aria-hidden="true" className="pointer-events-none absolute h-full w-full rounded-md bg-white" />
        //       <span
        //         aria-hidden="true"
        //         className={classNames(
        //           createNewProject ? 'bg-dataops-primary-blue' : 'bg-gray-200',
        //           'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
        //         )}
        //       />
        //       <span
        //         aria-hidden="true"
        //         className={classNames(
        //           createNewProject ? 'translate-x-5' : 'translate-x-0',
        //           'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
        //         )}
        //       />
        //     </Switch>
        //     <Label as="span" className="ml-1 cursor-default flex">
        //       <span className="text-sm font-medium text-gray-700">Create new data product</span>
        //       <InformationTooltip tooltip="Select an existing data product or create a new one" />
        //     </Label>
        //   </Field>
        <div className="col-span-2">
          <div className="w-fit mx-[auto] flex space-x-1 rounded-lg bg-slate-100 p-0.5">
            <button
              className={classNames(
                createNewProject ? 'bg-white shadow' : '',
                'flex flex-col items-center rounded-md py-1 pl-2 pr-2 text-sm font-semibold lg:pr-3',
              )}
              type="button"
              onClick={() => dispatch(setCreateNewProject(true))}
            >
              <span className="w-[7rem] text-sm lg:ml-2 text-gray-800 font-medium">New</span>
              <span className="w-[7rem] text-sm lg:ml-2 text-gray-800 font-medium">Data Product</span>
            </button>
            <button
              className={classNames(
                createNewProject ? '' : 'bg-white shadow',
                'flex flex-col items-center rounded-md py-1 pl-2 pr-2 text-sm font-semibold lg:pr-3',
              )}
              type="button"
              onClick={() => dispatch(setCreateNewProject(false))}
            >
              <span className="w-[7rem] text-sm lg:ml-2 text-gray-800 font-medium">Existing</span>
              <span className="w-[7rem] text-sm lg:ml-2 text-gray-800 font-medium">Data Product</span>
            </button>
          </div>
        </div>
      )}

      <div className="col-span-2 xl:col-span-1">
        <ComboboxAutocomplete
          label="DataOps group"
          options={groups}
          selected={selectedGroup}
          setSelected={(group: IGroup) => dispatch(setSelectedGroup(group))}
          query={groupSearchTerm}
          setQuery={setGroupSearchTerm}
          tooltip="Select the DataOps group to create the data product within"
        />
      </div>

      {props.showProjectSelector !== false && (
        <>
          {createNewProject && (
            <div className="col-span-2 xl:col-span-1">
              <label
                htmlFor="new-project-name"
                className="flex items-center text-sm font-medium leading-6 text-gray-900"
              >
                <div>New Data Product</div>
                <InformationTooltip tooltip="Enter a name for the DataOps project to create the data product within" />
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => {
                      const value = e.target.value;
                      dispatch(setNewProjectName(value));

                      // Clear any existing timeout
                      if (debounceTimeoutRef.current) {
                        clearTimeout(debounceTimeoutRef.current);
                      }

                      // Set a new timeout to validate the project name after a delay
                      debounceTimeoutRef.current = setTimeout(() => {
                        console.log('Validating sanitized value:', value);
                        const error = getProjectNameError(value);
                        setProjectNameError(error);
                      }, 1000); // 500ms debounce delay
                    }}
                    name="new-project-name"
                    id="new-project-name"
                    autoComplete="new-project-name"
                    className={classNames(
                      'block rounded-md flex-1 ring-1 ring-inset border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200',
                      projectNameError ? 'ring-red-500' : 'ring-gray-300',
                    )}
                    required
                    disabled={props.projectSelectorDisabled === true}
                  />
                </div>
                {projectNameError ? (
                  <p className="mt-1 min-h-[40px] text-sm text-red-600">{projectNameError}</p>
                ) : (
                  <div className="mt-1 h-[40px]"></div>
                )}
              </div>
            </div>
          )}

          {!createNewProject && (
            <div className="col-span-2 xl:col-span-1">
              <ComboboxAutocomplete
                label="Data Product"
                options={projectsInGroup}
                selected={selectedProject}
                setSelected={(project: IProject) => {
                  dispatch(setSelectedProject(project));
                  if (props.onProjectSelectionChanged) {
                    props.onProjectSelectionChanged();
                  }
                }}
                query={projectSearchTerm}
                setQuery={(value) => {
                  setProjectSearchTerm(value);
                  if (props.onProjectSearchChanged) {
                    props.onProjectSearchChanged();
                  }
                }}
                tooltip="Select the DataOps project to create the data product within"
              />
              <div className="mt-1 h-[44px]"></div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default GroupAndProjectSelector;
